import React from 'react';
import { startCase } from 'lodash';
import {
  AppointmentStatus,
  AppointmentType,
  BuildJsonType,
  EnhancedAppointment,
} from '@bb/domain/dist/types';
import { DescriptionText, Space, Tag, TagProps, Tooltip } from 'components';
import { correctShowingTypeLabel } from 'utils';
import styled from 'styled-components';

export type StatusText =
  | 'Cancelled'
  | 'Confirmed'
  | 'Denied'
  | 'Pending'
  | 'New'
  | 'Suggested'
  | 'Time Change'
  | 'Time Passed'
  | 'Attempted'
  | 'Removed'
  | 'Draft'
  | 'Awaiting Approval';

const StatusColourMap: Record<StatusText | string, TagProps['color']> = {
  Cancelled: 'red',
  Confirmed: 'green',
  Denied: 'red',
  Pending: 'blue',
  New: 'blue',
  Suggested: 'blue',
  'Time Change': 'blue',
  'Time Passed': 'default',
  Attempted: 'blue',
  Removed: 'default',
  Draft: 'default',
  'Awaiting Approval': 'blue',
};

export type StatusEntryProps = Pick<
  EnhancedAppointment,
  'listingContactTimeChange'
> & {
  isBuyer: boolean;
  type?: AppointmentType | 'APPOINTMENT' | 'ROUTE_STOP' | 'MANAGED_ACCESS';
  displayStatus: string;
  showTagOnly?: boolean;
};

const StatusEntry: React.FC<StatusEntryProps> = ({
  isBuyer,
  displayStatus,
  type,
  showTagOnly,
}) => {
  const tag = (
    <Tag
      data-cy="showing-status-text"
      color={StatusColourMap[displayStatus] || 'default'}
    >
      {displayStatus}
    </Tag>
  );

  if (showTagOnly) return tag;

  return (
    <StyledSpace>
      {isBuyer && displayStatus === 'Time Change' ? (
        <Tooltip title="Time change requested">
          <div>{tag}</div>
        </Tooltip>
      ) : (
        tag
      )}
      {type && (
        <DescriptionText data-cy="showing-contact">
          {type === 'APPOINTMENT'
            ? 'Buyer/Broker'
            : correctShowingTypeLabel(type)}
        </DescriptionText>
      )}
    </StyledSpace>
  );
};

const StyledSpace = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: start;
`;

export default StatusEntry;
